"use client";

import { forwardRef, useState } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import { Eye, EyeOff } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Icon } from "@/components/ui/Icon";

interface AuthInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  icon?: LucideIcon;
  type?: string;
  /** When type === 'password', show an eye toggle. */
  passwordToggle?: boolean;
  error?: string | null;
  /** Optional trailing element (overrides eye toggle if both provided). */
  trail?: ReactNode;
}

export const AuthInput = forwardRef<HTMLInputElement, AuthInputProps>(function AuthInput(
  { icon, type = "text", passwordToggle = false, error, trail, className, ...rest },
  ref,
) {
  const [showPw, setShowPw] = useState(false);
  const isPassword = type === "password";
  const effectiveType = isPassword && passwordToggle && showPw ? "text" : type;
  const showEye = isPassword && passwordToggle && !trail;

  return (
    <div className="mucka-auth-field">
      <div
        className={[
          "mucka-auth-input-wrap",
          !icon && "mucka-auth-input-wrap--no-icon",
          (showEye || trail) && "mucka-auth-input-wrap--has-trail",
          error && "mucka-auth-input-wrap--error",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {icon && (
          <span className="mucka-auth-input-icon">
            <Icon icon={icon} size={18} strokeWidth={2} />
          </span>
        )}
        <input
          ref={ref}
          className={["mucka-auth-input", className].filter(Boolean).join(" ")}
          type={effectiveType}
          {...rest}
        />
        {trail}
        {showEye && (
          <button
            type="button"
            className="mucka-auth-input-eye"
            onClick={() => setShowPw((v) => !v)}
            aria-label={showPw ? "Hide password" : "Show password"}
            tabIndex={-1}
          >
            <Icon icon={showPw ? EyeOff : Eye} size={18} />
          </button>
        )}
      </div>
      {error && <div className="mucka-auth-field-error">{error}</div>}
    </div>
  );
});
